<script setup lang="ts">
import { del, get, post, put, upload } from '@/api'
import EmailTemplateEditor from '@/@core/components/EmailTemplateEditor.vue'

definePage({
  meta: {
    action: 'manage',
    subject: 'Admin',
  },
})

const { snackbar } = useErrorHandler()
const route = useRoute('regle-email-templates-id')
const router = useRouter()
const templateId = route.params.id

interface Attachment {
  id: number
  original_name: string
  url: string
  mime_type: string
  size: number
  created_at: string
}

interface EmailTemplate {
  id: number
  name: string
  subject: string
  body: string
  is_active: boolean
  variables: string[]
  attachments: Attachment[]
}

const template = ref<EmailTemplate>({
  id: 0,
  name: '',
  subject: '',
  body: '',
  is_active: true,
  variables: [],
  attachments: [],
})

const loading = ref(false)
const saving = ref(false)
const previewing = ref(false)
const previewDialog = ref(false)
const previewData = ref({ subject: '', body: '' })
const testEmail = ref('')
const testPhone = ref('')
const testSending = ref(false)
const uploading = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)

const formatVariable = (name: string): string => `{{ $${name} }}`

const formatFileSize = (bytes: number): string => {
  if (bytes === 0)
    return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

const rules = {
  required: (value: string) => !!value || '이메일 주소를 입력해주세요.',
  email: (value: string) => /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(value) || '올바른 이메일 형식입니다.',
}

const fetchTemplate = async () => {
  loading.value = true
  try {
    const { success, data, status } = await get<EmailTemplate>(`/regle/email-templates/${templateId}`)
    if (success && data) {
      template.value = data
    }
    else if (status === 404) {
      snackbar.error('템플릿을 찾을 수 없습니다.')
      router.push({ name: 'regle-email-templates' })
    }
  }
  catch {
    snackbar.error('템플릿 정보를 불러오는데 실패했습니다.')
  }
  finally {
    loading.value = false
  }
}

const saveTemplate = async () => {
  saving.value = true
  try {
    const { success, error, status } = await put(`/regle/email-templates/${templateId}`, {
      subject: template.value.subject,
      body: template.value.body,
      is_active: template.value.is_active,
    })

    if (success) {
      snackbar.show('템플릿이 저장되었습니다.', 'success')
      router.push({ name: 'regle-email-templates' })
    }
    else if (status === 422 && error?.response?.data?.errors) {
      const validationErrors = error.response.data.errors

      const errorMessages = Object.entries(validationErrors)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join('\n')

      snackbar.show(`입력값을 확인해주세요:\n${errorMessages}`, 'error')
    }
    else {
      snackbar.error('템플릿 저장에 실패했습니다.')
    }
  }
  catch {
    snackbar.error('템플릿 저장에 실패했습니다.')
  }
  finally {
    saving.value = false
  }
}

const previewTemplate = async () => {
  previewing.value = true
  try {
    const sampleVariables: Record<string, string> = {}

    template.value.variables.forEach(v => {
      sampleVariables[v] = `[${v} 샘플값]`
    })

    const { success, data } = await post<{ subject: string; body: string }>(
      `/regle/email-templates/${templateId}/preview`,
      { variables: sampleVariables },
    )

    if (success && data) {
      previewData.value = data
      previewDialog.value = true
    }
  }
  catch {
    snackbar.error('미리보기에 실패했습니다.')
  }
  finally {
    previewing.value = false
  }
}

const sendTestEmail = async () => {
  testSending.value = true
  try {
    const variables: Record<string, string> = {}

    template.value.variables.forEach(v => {
      variables[v] = ''
    })

    // 테스트 전화번호 입력값을 {{ $phone }} 변수로 연결 (입력 안내와 일치)
    if (testPhone.value)
      variables.phone = testPhone.value

    const { success, data } = await post<{ message: string; failed_attachments?: string[] }>(
      `/regle/email-templates/${templateId}/test-send`,
      {
        email: testEmail.value,
        variables,
      },
    )

    if (success) {
      // 발송은 됐지만 첨부가 빠진 경우가 성공으로 묻히지 않도록 색을 구분한다
      const hasFailedAttachments = (data?.failed_attachments?.length ?? 0) > 0

      snackbar.show(
        data?.message || '테스트 이메일이 발송되었습니다.',
        hasFailedAttachments ? 'warning' : 'success',
      )
      testEmail.value = ''
    }
    else {
      snackbar.error('테스트 발송에 실패했습니다.')
    }
  }
  catch {
    snackbar.error('테스트 발송에 실패했습니다.')
  }
  finally {
    testSending.value = false
  }
}

/** upload() 헬퍼는 실패 시 응답 본문을 그대로 message 에 담는다. JSON 이면 message 필드를 꺼낸다. */
const extractErrorMessage = (raw?: string): string => {
  if (!raw)
    return ''

  try {
    const parsed = JSON.parse(raw)

    return typeof parsed?.message === 'string' ? parsed.message : ''
  }
  catch {
    return ''
  }
}

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (files && files.length > 0)
    uploadFile(files[0])

  target.value = '' // Reset input
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false

  const files = event.dataTransfer?.files
  if (files && files.length > 0)
    uploadFile(files[0])
}

const uploadFile = async (file: File) => {
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
  ]

  if (!allowedTypes.includes(file.type)) {
    snackbar.error('지원하지 않는 파일 형식입니다. (PDF, Word, Excel, 이미지 파일만 가능)')

    return
  }

  if (file.size > 10 * 1024 * 1024) {
    snackbar.error('파일 크기는 10MB를 초과할 수 없습니다.')

    return
  }

  uploading.value = true
  try {
    const formData = new FormData()

    formData.append('file', file)

    // upload() 헬퍼는 Authorization Bearer 헤더와 401 토큰 갱신 재시도를 처리한다.
    // (raw fetch 사용 시 Bearer 토큰이 누락되어 Sanctum 가드에서 401 발생)
    const { success, data, error } = await upload<{ success: boolean; data: Attachment }>(
      `/regle/email-templates/${templateId}/attachments`,
      formData,
    )

    if (success && data?.data) {
      template.value.attachments.push(data.data)
      snackbar.show('파일이 업로드되었습니다.', 'success')
    }
    else {
      // 서버는 실패 사유를 JSON 본문으로 돌려준다 (예: S3 저장 실패)
      snackbar.error(extractErrorMessage(error?.message) || '파일 업로드에 실패했습니다.')
    }
  }
  catch {
    snackbar.error('파일 업로드에 실패했습니다.')
  }
  finally {
    uploading.value = false
  }
}

const deleteAttachment = async (attachmentId: number) => {
  try {
    const { success } = await del(`/regle/email-templates/${templateId}/attachments/${attachmentId}`)

    if (success) {
      template.value.attachments = template.value.attachments.filter(a => a.id !== attachmentId)
      snackbar.show('첨부파일이 삭제되었습니다.', 'success')
    }
    else {
      snackbar.error('첨부파일 삭제에 실패했습니다.')
    }
  }
  catch {
    snackbar.error('첨부파일 삭제에 실패했습니다.')
  }
}

onMounted(fetchTemplate)
</script>

<template>
  <VRow v-if="!loading">
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>템플릿 수정: {{ template.name }}</span>
          <VBtn
            variant="text"
            prepend-icon="tabler-arrow-left"
            :to="{ name: 'regle-email-templates' }"
          >
            목록으로
          </VBtn>
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveTemplate">
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="template.subject"
                  label="이메일 제목"
                  placeholder="제목을 입력하세요"
                />
              </VCol>

              <VCol cols="12">
                <VCard
                  variant="outlined"
                  class="overflow-hidden"
                >
                  <VCardTitle class="text-subtitle-1 bg-grey-lighten-3 py-2">
                    이메일 본문
                  </VCardTitle>
                  <EmailTemplateEditor
                    v-model="template.body"
                    placeholder="이메일 본문을 입력하세요..."
                    :variables="template.variables"
                  />
                </VCard>
              </VCol>

              <!-- 첨부파일 섹션 -->
              <VCol cols="12">
                <VCard variant="outlined">
                  <VCardTitle class="text-subtitle-1 bg-grey-lighten-3 py-2 d-flex align-center">
                    <VIcon
                      icon="tabler-paperclip"
                      class="me-2"
                    />
                    첨부파일
                  </VCardTitle>
                  <VCardText>
                    <!-- 파일 업로드 영역 -->
                    <div
                      class="upload-zone pa-8 text-center border-dashed rounded cursor-pointer"
                      :class="{ 'bg-primary-lightest': isDragging }"
                      @click="triggerFileInput"
                      @dragover.prevent="isDragging = true"
                      @dragleave.prevent="isDragging = false"
                      @drop.prevent="handleDrop"
                    >
                      <input
                        ref="fileInput"
                        type="file"
                        class="d-none"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                        @change="handleFileSelect"
                      >
                      <VIcon
                        icon="tabler-cloud-upload"
                        size="48"
                        color="grey"
                        class="mb-2"
                      />
                      <p class="text-grey-darken-1 mb-1">
                        파일을 드래그하거나 클릭하여 업로드
                      </p>
                      <p class="text-caption text-grey">
                        PDF, Word, Excel, 이미지 (최대 10MB)
                      </p>
                      <VProgressLinear
                        v-if="uploading"
                        indeterminate
                        color="primary"
                        class="mt-2"
                      />
                    </div>

                    <!-- 첨부파일 목록 -->
                    <div
                      v-if="template.attachments.length > 0"
                      class="mt-4"
                    >
                      <VList density="compact">
                        <VListItem
                          v-for="attachment in template.attachments"
                          :key="attachment.id"
                          class="px-0"
                        >
                          <template #prepend>
                            <VIcon
                              :icon="attachment.mime_type.includes('pdf') ? 'tabler-file-type-pdf' : attachment.mime_type.includes('image') ? 'tabler-photo' : 'tabler-file'"
                              color="grey-darken-1"
                              class="me-2"
                            />
                          </template>
                          <VListItemTitle class="text-body-2">
                            {{ attachment.original_name }}
                          </VListItemTitle>
                          <VListItemSubtitle class="text-caption">
                            {{ formatFileSize(attachment.size) }}
                          </VListItemSubtitle>
                          <template #append>
                            <IconBtn
                              size="small"
                              variant="text"
                              color="error"
                              @click.stop="deleteAttachment(attachment.id)"
                            >
                              <VIcon icon="tabler-trash" />
                            </IconBtn>
                          </template>
                        </VListItem>
                      </VList>
                    </div>
                    <p
                      v-else
                      class="text-caption text-grey text-center mt-4"
                    >
                      첨부파일이 없습니다
                    </p>
                  </VCardText>
                </VCard>
              </VCol>

              <VCol cols="12">
                <VSwitch
                  v-model="template.is_active"
                  label="활성화 상태"
                  color="success"
                />
              </VCol>

              <!-- 테스트 발송 폼 -->
              <VCol cols="12">
                <VCard variant="outlined">
                  <VCardTitle class="text-subtitle-1 bg-grey-lighten-3 py-2">
                    <VIcon
                      icon="tabler-send"
                      class="me-2"
                    />
                    테스트 발송
                  </VCardTitle>
                  <VCardText>
                    <VForm @submit.prevent="sendTestEmail">
                      <VRow>
                        <VCol
                          cols="12"
                          md="6"
                        >
                          <VTextField
                            v-model="testEmail"
                            label="테스트 이메일 주소"
                            placeholder="test@example.com"
                            type="email"
                            :rules="[rules.required, rules.email]"
                          />
                        </VCol>
                        <VCol
                          cols="12"
                          md="6"
                        >
                          <VTextField
                            v-model="testPhone"
                            label="테스트 전화번호 (선택)"
                            placeholder="010-1234-5678"
                            hint="이메일 본문에 {{ $phone }} 변수가 있을 때 사용"
                          />
                        </VCol>
                      </VRow>
                      <VAlert
                        v-if="template.variables.length > 0"
                        type="info"
                        variant="tonal"
                        class="mt-4"
                      >
                        <span class="text-caption">사용 가능한 변수:</span>
                        <div class="mt-2">
                          <VChip
                            v-for="variable in template.variables"
                            :key="variable"
                            size="small"
                            class="me-1"
                          >
                            {{ formatVariable(variable) }}
                          </VChip>
                        </div>
                      </VAlert>
                      <div class="d-flex gap-2 mt-4">
                        <VBtn
                          type="submit"
                          color="primary"
                          :loading="testSending"
                          prepend-icon="tabler-send"
                        >
                          테스트 발송
                        </VBtn>
                        <VBtn
                          color="secondary"
                          variant="tonal"
                          @click="testEmail = ''; testPhone = ''"
                        >
                          초기화
                        </VBtn>
                      </div>
                    </VForm>
                  </VCardText>
                </VCard>
              </VCol>

              <VCol
                cols="12"
                class="d-flex gap-4"
              >
                <VBtn
                  type="submit"
                  color="primary"
                  :loading="saving"
                >
                  저장
                </VBtn>
                <VBtn
                  color="info"
                  variant="tonal"
                  :loading="previewing"
                  @click="previewTemplate"
                >
                  미리보기
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  :to="{ name: 'regle-email-templates' }"
                >
                  취소
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 미리보기 다이얼로그 -->
    <VDialog
      v-model="previewDialog"
      max-width="800"
    >
      <VCard>
        <VCardTitle>이메일 미리보기</VCardTitle>
        <VCardText>
          <div class="mb-4">
            <span class="text-subtitle-2">제목:</span>
            <p class="text-body-1 mt-1">
              {{ previewData.subject }}
            </p>
          </div>
          <VDivider class="mb-4" />
          <div class="mb-4">
            <span class="text-subtitle-2">본문:</span>
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="mt-2 pa-4 border rounded"
              v-html="previewData.body"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
          <div v-if="template.attachments.length > 0">
            <span class="text-subtitle-2">첨부파일:</span>
            <div class="mt-2">
              <VChip
                v-for="attachment in template.attachments"
                :key="attachment.id"
                size="small"
                class="me-1"
              >
                <VIcon
                  icon="tabler-paperclip"
                  start
                  size="14"
                />
                {{ attachment.original_name }}
              </VChip>
            </div>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            @click="previewDialog = false"
          >
            닫기
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>

  <!-- 로딩 상태 -->
  <VRow v-else>
    <VCol cols="12">
      <VCard>
        <VCardText class="text-center py-8">
          <VProgressCircular
            indeterminate
            color="primary"
          />
          <p class="mt-4 text-grey">
            템플릿 정보를 불러오는 중...
          </p>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped>
.upload-zone {
  border: 2px dashed rgba(var(--v-theme-on-surface), 0.2);
  transition: all 0.2s ease;
}

.upload-zone:hover {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.cursor-pointer {
  cursor: pointer;
}

.border-dashed {
  border-style: dashed !important;
}
</style>
